import { Link } from 'react-router-dom';
import { LegalPage } from './LegalPage';
import {
  AUTO_CONFIRM_HOURS,
  CANCELLATION_WINDOW_HOURS,
  LATE_CANCELLATION_PENALTY_PCT,
  LEGAL_CONTACT,
  LEGAL_VERSIONS,
  PLATFORM_COMMISSION_PCT,
} from '@makhe/shared';

export default function RefundsPage() {
  return (
    <LegalPage title="Payment, Refund & Cancellation Policy" lastUpdated={LEGAL_VERSIONS.refunds}>
      <p>
        This policy explains how payments, cancellations, and refunds work on Makhe.
        It forms part of our <Link to="/legal/terms">Terms of Service</Link>.
      </p>

      <h2>1. How payment works</h2>
      <p>
        When you book, Paystack places a <strong>hold</strong> on your card for the booking amount —
        the money stays in your account and isn&apos;t charged yet. You&apos;re only charged once the
        service is confirmed complete. Makhe&apos;s {PLATFORM_COMMISSION_PCT}% fee is included in the
        price shown at checkout. If a hold is never charged, it drops off your card automatically.
      </p>

      <h2>2. Buyer cancellation</h2>
      <ul>
        <li>
          <strong>More than {CANCELLATION_WINDOW_HOURS} hours</strong> before the scheduled start:
          the hold is released, no penalty.
        </li>
        <li>
          <strong>Within {CANCELLATION_WINDOW_HOURS} hours</strong> of an accepted booking:{' '}
          a {LATE_CANCELLATION_PENALTY_PCT}% cancellation fee may be charged; the rest of the hold is
          released.
        </li>
        <li>
          <strong>Safety cancellation:</strong> if you cancel for safety reasons using the in-app
          safety option, there&apos;s no penalty and the hold is released in full.
        </li>
      </ul>

      <h2>3. Seller cancellation or decline</h2>
      <p>
        If a creator declines or cancels an accepted booking, the hold on your card is released in
        full. Nothing was charged, so there&apos;s nothing to refund.
      </p>

      <h2>4. Completion &amp; auto-confirm</h2>
      <p>
        After the creator marks the service done, you have {AUTO_CONFIRM_HOURS} hours to confirm or
        raise a dispute. If you don&apos;t respond, the booking is confirmed automatically and your
        card is charged.
      </p>

      <h2>5. Disputes</h2>
      <p>
        Either party can open a dispute within 48 hours of the scheduled end time. While a booking is
        disputed, payment is paused until we&apos;ve reviewed it. Contact{' '}
        <a href={`mailto:${LEGAL_CONTACT.email}`}>{LEGAL_CONTACT.email}</a> with your booking ID.
      </p>

      <h2>6. No-shows (planned)</h2>
      <p>
        The following is our intended standard and may apply as the platform grows:
      </p>
      <ul>
        <li>
          <strong>Buyer no-show:</strong> up to 50% of the booking total may be charged and paid to
          the creator; Makhe waives its fee on that amount.
        </li>
        <li>
          <strong>Seller no-show:</strong> the hold is released in full; the creator gets a warning,
          and repeated no-shows lead to suspension.
        </li>
      </ul>
      <p>We&apos;ll let you know before any automated no-show charges take effect.</p>

      <h2>7. Refunds</h2>
      <p>
        Most cancellations simply release the hold — nothing was charged, so there&apos;s nothing to
        refund and the amount frees up with your bank within a few days. If you were already charged
        (for example, a dispute resolved in your favour after the service), the refund goes back to
        your original payment method via Paystack, usually within 3–10 business days.
      </p>
    </LegalPage>
  );
}
